import { cva, type VariantProps } from 'class-variance-authority'
import type { DefaultNamespace } from 'i18next'
import type {
	ComponentPropsWithRef,
	HTMLInputTypeAttribute,
	InputHTMLAttributes,
	ReactNode,
	RefCallback,
	RefObject
} from 'react'
import { InputHelpText } from '@/components/ui/input-help-text'
import { cn } from '@/helpers/utils'

/**
 * The field box: border, height and the hover / focus / error / disabled
 * states. Kept here rather than on `components/ui/input`, which is the plain
 * shadcn primitive and stays as installed.
 */
const floatingInputVariants = cva(
	cn(
		'number-input rounded-xl border-0 bg-white px-4 py-0 text-grey-1000 shadow-[0_0_0_1.5px_var(--color-grey-600)] outline-none transition-shadow',
		// placeholder
		'placeholder:text-grey-700 placeholder:disabled:text-grey-500 data-placeholder:data-disabled:text-grey-500 data-placeholder:text-grey-700 data-placeholder:disabled:text-grey-500 placeholder:data-disabled:text-grey-500',
		// focus
		'focus:shadow-[0_0_0_2px_var(--color-grey-1000)] focus:outline-none aria-invalid:focus:shadow-[0_0_0_1.5px_var(--color-red-800)]',
		// popup open
		'data-popup-open:shadow-[0_0_0_2px_var(--color-grey-1000)] aria-invalid:data-popup-open:shadow-[0_0_0_2px_var(--color-red-800)]',
		// hover
		'hover:not-disabled:shadow-[0_0_0_1.5px_var(--color-grey-1000)] aria-invalid:hover:shadow-[0_0_0_1.5px_var(--color-red-800)] hover:data-disabled:shadow-[0_0_0_1.5px_var(--color-grey-400)]! hover:data-read-only:shadow-[0_0_0_1px_var(--color-grey-400)]',
		// disabled — wins over invalid (a locked field must not show error styling)
		'disabled:cursor-not-allowed disabled:bg-grey-100! disabled:text-grey-500 disabled:shadow-[0_0_0_1.5px_var(--color-grey-400)]!',
		'data-disabled:cursor-not-allowed data-disabled:bg-grey-100! data-disabled:text-grey-500 data-disabled:shadow-[0_0_0_1.5px_var(--color-grey-400)]!',
		// aria-invalid
		'aria-invalid:bg-red-100 aria-invalid:shadow-[0_0_0_1.5px_var(--color-red-700)]',

		// read-only
		'data-read-only:bg-grey-100 data-read-only:text-grey-700 data-read-only:shadow-[0_0_0_1px_var(--color-grey-400)]'
	),
	{
		variants: {
			size: {
				sm: 'h-10.5 text-md', // 42px
				md: 'h-12 text-sm/4.5', // 48px
				lg: 'h-15 text-md/5' // 60px
			}
		},
		defaultVariants: {
			size: 'md'
		}
	}
)

export type FloatingInputSize = NonNullable<
	VariantProps<typeof floatingInputVariants>['size']
>

export interface FloatingInputProps
	extends InputHTMLAttributes<HTMLInputElement> {
	className?: string
	label?: string | DefaultNamespace
	name?: string
	type: HTMLInputTypeAttribute
	startSlot?: ReactNode
	endSlot?: ReactNode
	errorMessage?: string | DefaultNamespace
	helpText?: string | DefaultNamespace
	placeholder?: string
	inputSize?: FloatingInputSize
	/**
	 * Props forwarded to the field box. When this component is used as a
	 * `render` target (e.g. a Select/Combobox trigger), the host primitive
	 * injects its interaction props (onClick, aria-*, data-*, ref) here so the
	 * box becomes the actual trigger element.
	 */
	rootProps?: ComponentPropsWithRef<'div'> & { 'data-read-only'?: boolean }
	ariaInvalid?: boolean
	ref?:
		| RefCallback<HTMLInputElement | null>
		| RefObject<HTMLInputElement | null>
}

/**
 * Text input whose label sits inside the box: centred while the field is
 * empty, floated to the top once it has a value or focus.
 *
 * Help text and the error render under the box, the error replacing the help
 * text, the same way `InputText` does.
 */
const FloatingInput = ({
	label,
	name,
	id,
	type,
	startSlot,
	endSlot,
	errorMessage,
	helpText,
	className,
	disabled,
	readOnly,
	placeholder,
	inputSize = 'md',
	ref,
	rootProps,
	ariaInvalid,
	...rest
}: FloatingInputProps) => {
	const { className: rootClassName, ...restRootProps } = rootProps ?? {}
	const inputId = id ?? name
	const isInvalid = ariaInvalid || !!errorMessage

	return (
		<div className={cn('flex flex-col gap-1.5', className)}>
			<div
				className={cn(
					'group relative flex items-center gap-3',
					floatingInputVariants({ size: inputSize }),
					// disabled — grey out everything inside (start/end slots, icons, buttons, label)
					'data-disabled:**:cursor-not-allowed data-disabled:**:text-grey-500',
					'data-read-only:[&>button]:text-grey-500 data-read-only:[&>svg]:text-grey-500',
					rootClassName
				)}
				data-disabled={disabled || undefined}
				data-invalid={isInvalid || undefined}
				data-read-only={readOnly || undefined}
				{...restRootProps}
			>
				{startSlot}
				<div className='relative w-full min-w-0'>
					<input
						aria-invalid={isInvalid || undefined}
						className={cn(
							'peer number-input w-full bg-transparent pt-5 pb-2 outline-none placeholder:text-transparent',
							'disabled:cursor-not-allowed'
						)}
						disabled={disabled}
						id={inputId}
						name={name}
						// A non-empty placeholder is what `:placeholder-shown`
						// needs to tell an empty field from a filled one; it is
						// rendered transparent, the label stands in for it.
						placeholder={placeholder || ' '}
						readOnly={readOnly}
						ref={ref}
						type={type}
						{...rest}
					/>

					{label && (
						<label
							className={cn(
								'pointer-events-none absolute left-0 transition-all',

								// default (placeholder state)
								'top-1/2 -translate-y-1/2 text-grey-700',

								// when filled (content-based, works for readOnly triggers too)
								'peer-not-placeholder-shown:top-3 peer-not-placeholder-shown:text-[10px] peer-not-placeholder-shown:leading-3',

								// when focused — only when editable, not when used as a Select/Combobox trigger
								!readOnly &&
									'peer-focus:top-3 peer-focus:text-[10px] peer-focus:leading-3',

								inputSize === 'lg' &&
									'peer-not-placeholder-shown:text-xs/4.5',
								inputSize === 'lg' &&
									!readOnly &&
									'peer-focus:text-xs/4.5',

								// error color
								'group-data-invalid:text-red-700 group-data-invalid:group-hover:text-red-800 group-data-invalid:group-focus-within:text-red-800',

								// disabled color
								'group-data-disabled:text-grey-500!',

								// read-only color
								'group-data-read-only:text-grey-700!'
							)}
							htmlFor={inputId}
						>
							{label}
						</label>
					)}
				</div>
				{endSlot}
			</div>
			<InputHelpText error={errorMessage} helpText={helpText} />
		</div>
	)
}

export { FloatingInput }
